import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { COLLECTIONS } from "@/constants/firestoreCollections";
import type {
  AdminSettingsDoc,
  StoreSettings,
  BusinessHours,
  FiscalSettings,
  NotificationSettings,
  SocialMediaSettings,
  FinancingSettingsForm,
  WarrantySettings,
} from "@/types/adminSettings";
import { DEFAULT_BUSINESS_HOURS } from "@/types/adminSettings";

const ADMIN_SETTINGS_ID = "main";

function defaultStore(): StoreSettings {
  return {
    storeName: "",
    storePhone: "",
    storeEmail: "",
    storeAddress: "",
    storeCity: "",
    location: { latitude: 18.4861, longitude: -69.9312 },
    businessHours: DEFAULT_BUSINESS_HOURS,
  };
}

function defaultFinancing(): FinancingSettingsForm {
  return {
    isEnabled: true,
    requiresApproval: true,
    maxFinancingAmount: 500000,
    minCreditScore: 650,
    baseInterestRate: 18.5,
  };
}

/** Obtiene las configuraciones administrativas (mismo doc que usa la app móvil). */
export async function getAdminSettings(): Promise<AdminSettingsDoc | null> {
  const ref = doc(db, COLLECTIONS.ADMIN_SETTINGS, ADMIN_SETTINGS_ID);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as AdminSettingsDoc;
}

/** Crea el documento de configuración por defecto si no existe. */
export async function ensureAdminSettings(): Promise<AdminSettingsDoc> {
  const existing = await getAdminSettings();
  if (existing) return existing;
  const ref = doc(db, COLLECTIONS.ADMIN_SETTINGS, ADMIN_SETTINGS_ID);
  const defaultDoc: AdminSettingsDoc = {
    store: defaultStore(),
    fiscal: { rnc: "", companyName: "" },
    notifications: {
      emailNotifications: true,
      whatsappNotifications: true,
      pushNotifications: true,
    },
    socialMedia: {},
    financing: defaultFinancing() as FinancingSettingsForm & Record<string, unknown>,
    warranty: {
      moduleEnabled: true,
      defaultWarrantyText: "1 año o 20,000 km",
      warrantyContactEmail: "",
      warrantyContactPhone: "",
    },
  };
  await setDoc(ref, {
    ...defaultDoc,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: ADMIN_SETTINGS_ID, ...defaultDoc };
}

/** Actualiza solo las claves enviadas (merge). */
export async function updateAdminSettings(
  updates: Partial<Omit<AdminSettingsDoc, "id">>
): Promise<void> {
  const ref = doc(db, COLLECTIONS.ADMIN_SETTINGS, ADMIN_SETTINGS_ID);
  await updateDoc(ref, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function updateStoreSettings(store: StoreSettings): Promise<void> {
  const current = await getAdminSettings();
  const existingStore = (current?.store ?? {}) as Record<string, unknown>;
  await updateAdminSettings({
    store: { ...existingStore, ...store },
  });
}

export async function updateBusinessHours(businessHours: BusinessHours): Promise<void> {
  const current = await getAdminSettings();
  if (!current?.store) return;
  await updateAdminSettings({
    store: { ...current.store, businessHours },
  });
}

export async function updateFiscalSettings(fiscal: FiscalSettings): Promise<void> {
  const current = await getAdminSettings();
  const existing = (current?.fiscal ?? {}) as Record<string, unknown>;
  await updateAdminSettings({ fiscal: { ...existing, ...fiscal } });
}

export async function updateNotificationSettings(
  notifications: NotificationSettings
): Promise<void> {
  const current = await getAdminSettings();
  const existing = (current?.notifications ?? {}) as Record<string, unknown>;
  await updateAdminSettings({ notifications: { ...existing, ...notifications } });
}

export async function updateSocialMediaSettings(
  socialMedia: SocialMediaSettings
): Promise<void> {
  const current = await getAdminSettings();
  const existing = (current?.socialMedia ?? {}) as Record<string, unknown>;
  await updateAdminSettings({ socialMedia: { ...existing, ...socialMedia } });
}

/** Actualiza financiamiento; preserva el resto del objeto financing en Firestore. */
export async function updateFinancingSettings(
  financing: Partial<FinancingSettingsForm>
): Promise<void> {
  const current = await getAdminSettings();
  const existing = (current?.financing ?? {}) as Record<string, unknown>;
  await updateAdminSettings({
    financing: { ...existing, ...financing },
  });
}

/** Actualiza configuración de garantías. */
export async function updateWarrantySettings(warranty: WarrantySettings): Promise<void> {
  const current = await getAdminSettings();
  const existing = (current?.warranty ?? {}) as Record<string, unknown>;
  await updateAdminSettings({
    warranty: { ...existing, ...warranty },
  });
}
